import moment from 'moment';
import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {user} from '../../../store/reducers/user';
import Notes from '../../../util/notes';
import Formatter from '../../../util/formatter';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import AgyleSelectField from '../../../components/ui/selector/agyleSelectField';
import {CLINICAL_COURSE_BASE, CLINICAL_COURSE_OPTIONS} from './formsUtil';
import {every} from 'lodash';

const ClinicalCourse = forwardRef((props, ref) => {
    const {t} = useTranslation();

    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    const [formData, setFormData] = useState(props.patient?.studies?.clinicalCourse || CLINICAL_COURSE_BASE());
    const [formIsModified, setFormIsModified] = useState(false);

    useEffect(() => {
        if (props.onDisableSubmit && !props.disableSave) {
            const {clinicalCourse, actionsGivenStatus, actionsGiven, adviceGiven} = formData;
            props.onDisableSubmit(every([clinicalCourse, actionsGivenStatus, actionsGiven, adviceGiven], val => val === ''));
        }
    }, [formData]);

    useEffect(() => {
        if (formIsModified && props.onModify) props.onModify();
    }, [formIsModified]);

    useImperativeHandle(ref, () => ({
        submitForm: submit,
    }));

    useEffect(() => {
        if (props.onSubmitFunction) {
            props.onSubmitFunction(submit);
        }
    }, [formData]);

    const getNote = () => {
        return Notes.getClinicalCourseNote(formData, props.patientType);
    };

    const createObj = (date) => {
        return {
            who: userFullName,
            whoLogin: userObject?.userName,
            when: date,
            clinicalCourse: formData.clinicalCourse,
            actionsGivenStatus: formData.actionsGivenStatus,
            actionsGiven: formData.actionsGiven,
            adviceGiven: formData.adviceGiven,
        };
    };

    function submit() {
        const date = moment().toISOString();

        if (!props.disableSave) {
            props.onChange([{
                name: 'studies.clinicalCourse',
                value: createObj(date),
                noteToAdd: getNote(),
            }]);
        }
    }

    return (
        <>
            <AgyleSelectField
                label={t('Status')}
                value={formData.actionsGivenStatus || ''}
                onChange={(event) => {
                    setFormData((formData) => ({
                        ...formData,
                        actionsGivenStatus: event,
                    }));
                    setFormIsModified(true);
                }}
                withBackground
                withNewBackground
                withBorder
                shrink
                options={CLINICAL_COURSE_OPTIONS}
                size="large"
            />

            <AgyleTextField
                label={t('Plan and Requested Actions Given')}
                rows={8}
                multiline
                onChange={(event) => {
                    setFormData((formData) => ({
                        ...formData,
                        actionsGiven: event.target.value,
                    }));
                    setFormIsModified(true);
                }}
                value={formData.actionsGiven || ''}
                fullWidth
                noBorder={false}
                withNewBackground
            />

            <AgyleTextField
                label={t('Information and Advice Given')}
                rows={8}
                multiline
                onChange={(event) => {
                    setFormData((formData) => ({
                        ...formData,
                        adviceGiven: event.target.value,
                    }));
                    setFormIsModified(true);
                }}
                value={formData.adviceGiven || ''}
                fullWidth
                noBorder={false}
                withNewBackground
            />

            <AgyleTextField
                label={t('Message to GP')}
                rows={8}
                multiline
                onChange={(event) => {
                    setFormData((pr) =>
                        ({
                            ...pr,
                            clinicalCourse: event.target.value,
                        }),
                    );
                    setFormIsModified(true);
                }}
                value={formData.clinicalCourse}
                debounce
                fullWidth
                noBorder={false}
                withNewBackground
            />
        </>
    );
});

export default ClinicalCourse;
